import { useCallback, useState, useEffect, useRef } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppSelector, useAppDispatch, DriveRequest, ErrorStackItem, Message, getEnumKey, MessageHandlerDisplayType, useSendWatchRequestMutation, DriveErrorMessages,
  DriveWatchedErrorMessages, CollectionFilesyncAPI, WatchedLogList, useGetPermissions, PermissionSetType, useGetGrants, GenericNode, GrantSet, GrantSetType, DirectoryInfo,
  getDirSymmKey, GenericEntry, WatchedLogEntity, WatchedLogEntry, WatchedNodeLog, WatchedLogsEntity, DateRangeFilter, MaintainerGrantsMap, PermissionSetTypeMap, DirectoryInfoMap,
  GenericEntryEntity
} from "src/common";
import { uiActions, DriveCallbackAsyncFunction } from "src/store";
import { RootState } from "src/store/configureStore";


export type WatchedEntryMap = Map<string, GenericEntry>;
export function useFetchWatchedActivity(current_account: Account, entry?: GenericEntryEntity, limit?: number, include_metadata: boolean = false) {
    const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
    const default_grants = useAppSelector((state: RootState) => state.drive.default_grants);
    const [watched_activity, setWatchedActivity] = useState<WatchedLogsEntity | undefined>();
    const [error, setError] = useState<boolean>(false);
    const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);
    const { grants, error: grant_error, getGrants, destroyGrants } = useGetGrants(current_account);
    const [sendWatchRequest] = useSendWatchRequestMutation();
    const dispatch = useAppDispatch();

    // Description: hold working nodes and node information
    const nodes_ref = useRef<WatchedNodeLog[]>([]);
    const working_node_ref = useRef<WatchedNodeLog>();
    const node_queue_ref = useRef<WatchedLogEntity[]>([]);
    const total_count_ref = useRef<number>(0);

    // Description: Intermediate holds information while building the nodes
    const watched_list_info = useRef<WatchedLogList | undefined>();
    function setWatchedListInfo(_watched_list_info?: WatchedLogList) {
        watched_list_info.current = _watched_list_info;
    }
    // Maps
    const watched_entries_ref = useRef<WatchedEntryMap>(new Map<string, GenericEntry>);
    function setWatchedEntries(id: string, _watched_entry: GenericEntry) {
        watched_entries_ref.current.set(id, _watched_entry);
    }
    const maintainer_grants_ref = useRef<MaintainerGrantsMap>(new Map<string, GrantSetType>);
    function setMaintainerGrantsRef(id: string, maintainer_grants: GrantSetType) {
        maintainer_grants_ref.current.set(id, maintainer_grants);
    }
    const permission_sets_ref = useRef<PermissionSetTypeMap>(new Map<string, PermissionSetType>);
    function setPermissionSetsRef(id: string, maintainer_grants: PermissionSetType) {
        permission_sets_ref.current.set(id, maintainer_grants);
    }
    function getPermissionSetRef(collection_id: string): PermissionSetType | undefined {
        return permission_sets_ref.current.get(collection_id);
    }
    const directory_info_ref = useRef<DirectoryInfoMap>(new Map<string, DirectoryInfo>);
    function setDirectoryInfoRef(id: string, directory_info: DirectoryInfo) {
        directory_info_ref.current.set(id, directory_info);
    }

    // Description: Allow active permissions and default_permissions to be accessed
    const default_permissions_ref = useRef<PermissionSetType[]>(default_permissions);
    useEffect(() => {
        default_permissions_ref.current = default_permissions;
    }, [default_permissions]);

    // Description: Handle the name field in the watch node list
    useEffect(() => {
        if (!!permissions && !!working_node_ref.current) {
            setPermissionSetsRef(permissions.collection_id.B64(), permissions);
            destroyPermissions(); // NOTE: Return the permission state to undefined to be able to detect future changes
            handleWatchNodePermissions(working_node_ref.current, permissions);
        }
    }, [permissions]);

    // Description: useEffect hook for getting grant per row
    useEffect(() => {
        if (!!grants) {
            setMaintainerGrantsRef(grants.id, grants);
            if (!!working_node_ref.current) {
                const permissions_ref = getPermissionSetRef(working_node_ref.current.getCollectionId_asB64());
                !!permissions_ref ? handleWatchNodeGrants(working_node_ref.current, permissions_ref, grants) :
                    handleItemError(working_node_ref.current, DriveErrorMessages.default, { stack_error: { data: working_node_ref.current.toObject() }, stack_message: DriveErrorMessages.error_sending_request });
            } else {
                handlePageErrorMessage(DriveErrorMessages.default, { stack_message: `missing working_node_ref on grant fetch: Collection_id: ${grants.collection_id}` });
            };
        }

        if (!!grant_error) {
            !!working_node_ref.current ?
                handleItemError(working_node_ref.current, DriveErrorMessages.default, { stack_error: { data: working_node_ref.current.toObject() }, stack_message: DriveErrorMessages.error_sending_request }) :
                handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_grant });
        };
    }, [grants, grant_error]);

    // Description: Fetch Watch activity counts separate
    // NOTE: Only send pagination information for list view (not for details)
    async function handleWatchActivityCounts(page_index: number = 0, date_range?: DateRangeFilter, user_filter?: string[]) {
        const drive_request = await CollectionFilesyncAPI.getWatchLogs(current_account, date_range, user_filter, page_index, entry?.collection_id,
            entry?.id, 1, include_metadata, true);
        async function callback(message: FSMessage) {
            if (message.getStatus() === FSStatus.OK) {
                total_count_ref.current = message?.getTotalCount() || 0;
                handleWatchActivity(page_index, date_range, user_filter);
            }
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_fetching_watched_root_nodes);
    }

    // Description: Fetch all watched root nodes
    async function handleWatchActivity(page_index: number = 0, date_range?: DateRangeFilter, user_filter?: string[]) {
        const drive_request = await CollectionFilesyncAPI.getWatchLogs(
            current_account,
            date_range,
            user_filter,
            page_index,
            entry?.collection_id,
            entry?.id,
            limit,
            include_metadata);
        async function callback(message: FSMessage) {
            if (message.getStatus() === FSStatus.OK) {
                const list = new WatchedLogList(message);
                setWatchedListInfo(list);
                nodes_ref.current = list._nodes.slice();
                getNext();
            } else {
                handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_fetching_watched_root_nodes }, message);
            }
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_fetching_watched_root_nodes);
    }

    // Description: Handle the name field in the watch node list
    async function handleWatchNodePermissions(log: WatchedNodeLog, _permissions: PermissionSetType) {
        const node = log.getNode();
        if (!!node) {
            const maintainer_id = node.getMaintainerId_asB64();
            if (!!maintainer_id) {
                // NOTE: Do a check for cached assets to bypass getting same collection again
                const _maintainer_grants = maintainer_grants_ref.current.get(maintainer_id);
                !!_maintainer_grants ? handleWatchNodeGrants(log, _permissions, _maintainer_grants) :
                    getGrants(maintainer_id, node.getCollectionId_asB64(), default_permissions_ref.current, default_grants);
            } else {
                !!node.getParentId_asB64() ? fetchDirectoryInfo(node, log, _permissions) : handleDecryptName(node, log, _permissions);
            }
        } else {
            handleItemError(log, DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_maintainer_directory });
        }
    }

    // Description: Handle the name field in the watch node list
    async function handleWatchNodeGrants(log: WatchedNodeLog, _permissions: PermissionSetType, _grants: GrantSetType) {
        const node = log.getNode();
        if (!!node) {
            const id = node.getId_asB64();
            const maintainer_id = node.getMaintainerId_asB64();
            // NOTE: If the node is missing the getWrappedDirKey get its parent (ie files uploaded through FS)
            if (!!maintainer_id && id !== maintainer_id) {
                fetchDirectoryInfo(node, log, _permissions, _grants);
            } else {
                handleDecryptName(node, log, _permissions, _grants);
                destroyGrants();
            }
        } else {
            handleItemError(log, DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_maintainer_directory });
        }
    }

    // Description: Handle Parent information for those rows requiring parent symm key
    async function fetchDirectoryInfo(node: GenericNode, log: WatchedNodeLog, _permissions: PermissionSetType, _grants?: GrantSetType) {
        const parent_id = node.getParentId_asB64();
        // NOTE: Check on cached parent directory_info
        const _working_parent_dir = directory_info_ref.current.get(parent_id);
        if (!!_working_parent_dir) {
            handleDecryptName(node, log, _permissions, _grants, _working_parent_dir);
            return;
        }
        // NOTE: Only get this if _working_parent_dir is not saved
        const collection_id = node.getCollectionId_asB64();
        const request = await CollectionFilesyncAPI.getDirectoryInformationRequest(collection_id, parent_id, true);
        const drive_request = await DriveRequest.initializeRequest(request, current_account, _permissions);
        async function callback(message: FSMessage) {
            if (message.getStatus() === FSStatus.OK) {
                const dir = message.getDir();
                if (!!dir) {
                    const wrapped_dir_key = dir.getWrappedDirKey();
                    const key_version = dir.getWrappedDirKey()?.getKeyVersion() || 0;
                    const grant = !!_grants ? GrantSet.getGrantByRole(_grants, key_version) : undefined;
                    const reader_user_key = !!grant ? await grant.key(current_account) : undefined;
                    const symmetric_key = !!wrapped_dir_key ? await getDirSymmKey(wrapped_dir_key, current_account, _permissions, reader_user_key) : undefined;
                    if ((!!wrapped_dir_key && !!symmetric_key)) {
                        const working_parent_dir = {
                            symmetric_key,
                            wrapped_dir_key,
                            version: dir.getVersion_asB64(),
                            grants: _grants
                        };
                        setDirectoryInfoRef(parent_id, working_parent_dir);
                        handleDecryptName(node, log, _permissions, _grants, working_parent_dir);
                    } else {
                        handleItemError(log, DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_maintainer_directory }, message);
                    }
                } else {
                    handleItemError(log, DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_maintainer_directory }, message);
                }
            } else {
                handleItemError(log, DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_maintainer_directory }, message);
            }
        }
        handleSendRequest(drive_request, callback, DriveErrorMessages.error_fetching_maintainer_directory);
    }

    // Description:  Handle entry name for all types and move to next item
    // NOTE: Handle the parent DirectoryInfo (symm key)
    async function handleDecryptName(node: GenericNode, log: WatchedNodeLog, _permissions: PermissionSetType, _grants?: GrantSetType, _directory_info?: DirectoryInfo) {
        const _entry = await GenericEntry.init(node, current_account, _permissions, _grants, _directory_info);
        //  NOTE: Cache successful entry instances to bypass the whole process when possible
        setWatchedEntries(_entry.id, _entry);
        setLogsContinue(log, _entry);
    }

    // Description: Set Logs and get Next
    function setLogsContinue(log: WatchedNodeLog, _entry: GenericEntry) {
        const watched_log_entity = new WatchedLogEntry(log, _entry, false, !!entry);
        node_queue_ref.current.push(watched_log_entity.item);
        getNext();
    }

    // Description:  Handle Failed rows and creates generic row for proper page size, row counts and pagination, 
    async function buildErrorEntry(log: WatchedNodeLog) {
        const _entry = await GenericEntry.initErrorItem(log.getNode());
        const watched_log_entity = new WatchedLogEntry(log, _entry, true);
        node_queue_ref.current.push(watched_log_entity.item);
    }

    // Description: Move to next item in queue
    function getNext() {
        working_node_ref.current = nodes_ref.current.shift();
        if (!!working_node_ref.current) {
            // NOTE: Use  pre-cached entries
            const saved_entry = watched_entries_ref.current.get(working_node_ref.current.getNodeId_asB64());
            if (!!saved_entry) {
                setLogsContinue(working_node_ref.current, saved_entry);
            } else {
                const _collection_id = working_node_ref.current.getCollectionId_asB64();
                // NOTE: Use pre-cached permissions when there
                const _permissions = getPermissionSetRef(_collection_id);
                !!_permissions ? handleWatchNodePermissions(working_node_ref.current, _permissions) :
                    getPermissions(_collection_id, default_permissions);
            }

        } else {
            handleSuccess();
        }
    }

    // Description: Complete fetch method
    function handleSuccess() {
        if (!!watched_list_info.current) {
            watched_list_info.current.entries = node_queue_ref.current;
            total_count_ref.current > 0 && watched_list_info.current.setPaginationInfo(total_count_ref.current);
            setWatchedActivity(watched_list_info.current.item);
        } else {
            handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_fetching_watched_root_nodes }, null);
        }
    }

    // -----------------------------
    // HOOK General
    // -----------------------------
    // Description: Sends Request and handles error when the request object is null, pass it custom stack message
    function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>,
        stack_error: string = DriveErrorMessages.error_sending_request, user_message: string = DriveErrorMessages.default) {
        if (!!request) {
            request.setCallback(callback);
            sendWatchRequest(request);
        } else {
            handlePageErrorMessage(user_message, { stack_error, stack_message: DriveErrorMessages.error_sending_request }, null, MessageHandlerDisplayType.logger);
        }
    }

    // Description: Handle error message and send error to store
    function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null,
        display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useFetchWatchedActivity Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
        setError(true);
    }

    // Description:  Handle queue item error
    function handleItemError(log: WatchedNodeLog, message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null) {
        buildErrorEntry(log);
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useFetchWatchedActivity Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
        destroyGrants(); // NOTE: destroy grants to allow for a next iteration
        getNext();
    }

    // Description: reset hook 
    function reset() {
        nodes_ref.current = [];
        node_queue_ref.current = [];
        working_node_ref.current = undefined;
        watched_list_info.current = undefined;
        watched_entries_ref.current = new Map<string, GenericEntry>;
        total_count_ref.current = 0;
        setWatchedListInfo(undefined);
        setError(false);
        destroyGrants();
        destroyPermissions();
    }

    // -----------------------------
    // Callbacks
    // -----------------------------
    // Description: Callback for browse directory
    const fetchWatchedActivity = useCallback((page: number = 1, date_range?: DateRangeFilter, user_filter?: string[]) => {
        reset();
        const page_index = Number(page - 1);
        !!entry ?
            handleWatchActivity(page_index, date_range, user_filter) :
            handleWatchActivityCounts(page_index, date_range, user_filter);
        ;
    }, []);

    // Description: reset hook 
    const resetWatchedActivity = useCallback(() => {
        reset();
    }, []);

    return {
        fetchWatchedActivity,
        resetWatchedActivity,
        watched_activity,
        error
    };
}
